// UI-only form modal for admin plan configs: create and edit share one form.
// The page owns data and mutations; the modal just collects values (cf.
// DocumentFormModal). Money fields are edited in major units (USD) and
// converted to integer cents on submit; storage is edited in GiB steps and
// converted to exact bytes — the same numbers the admin sees in the table.
import { useEffect, type ReactElement } from 'react';
import { Alert, Form, Input, InputNumber, Modal } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';

import { ApiError } from '../../../shared/api-client/index.js';
import type { AdminPlanConfig } from '../api/admin.client.js';
import { i18n } from '../../../i18n/index.js';

export interface PlanFormValues {
  code: string;
  displayName: string;
  price: number | null;
  cost: number | null;
  tokenLimitMonthly: number | null;
  storageGib: number | null;
  sortOrder: number;
}

export interface AdminPlanFormSubmitInput {
  code: string;
  displayName: string;
  priceCents: number;
  costCents: number;
  tokenLimitMonthly: number;
  storageLimitBytes: number;
  billingPeriod: string;
  sortOrder: number;
}

export const GIB = 2 ** 30;

/** Cents ↔ major units (USD) and bytes ↔ GiB, kept next to the form contract. */
export function dollarsToCents(value: number): number {
  return Math.round(value * 100);
}

export function centsToDollars(cents: number): number {
  return cents / 100;
}

export function gibToBytes(value: number): number {
  return Math.round(value * GIB);
}

export function bytesToGib(bytes: string): number {
  return Number(bytes) / GIB;
}

type Translate = (key: string) => string;

/** Maps plan form errors to localized messages (conflict = duplicate code). */
function planFormErrorMessage(err: unknown, t: Translate): string {
  if (err instanceof ApiError && err.status === 409) {
    return t('crm.admin.plans.error_code_exists');
  }
  return t('crm.admin.plans.error_save_failed');
}

export interface AdminPlanFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Passed → edit mode (code is locked); otherwise create. */
  plan: AdminPlanConfig | null;
  pending: boolean;
  submitError: unknown;
  onSubmit: (input: AdminPlanFormSubmitInput) => void;
}

export function AdminPlanFormModal({
  open,
  onClose,
  plan,
  pending,
  submitError,
  onSubmit,
}: AdminPlanFormModalProps): ReactElement {
  const { t } = useTranslation();
  const [form] = Form.useForm<PlanFormValues>();
  const isEdit = plan !== null;

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      code: plan?.code ?? '',
      displayName: plan?.displayName ?? '',
      price: plan === null ? null : centsToDollars(plan.priceCents),
      cost: plan === null ? null : centsToDollars(plan.costCents),
      tokenLimitMonthly: plan === null ? null : Number(plan.tokenLimitMonthly),
      storageGib: plan === null ? null : bytesToGib(plan.storageLimitBytes),
      sortOrder: plan?.sortOrder ?? 0,
    });
  }, [open, plan, form]);

  function handleSubmit(values: PlanFormValues): void {
    onSubmit({
      code: values.code.trim().toLowerCase(),
      displayName: values.displayName.trim(),
      priceCents: dollarsToCents(values.price ?? 0),
      costCents: dollarsToCents(values.cost ?? 0),
      tokenLimitMonthly: Math.max(0, Math.round(values.tokenLimitMonthly ?? 0)),
      storageLimitBytes: gibToBytes(Math.max(0, values.storageGib ?? 0)),
      // Subscriptions are monthly (owner decision 2026-10-20); the column
      // exists for future flexibility, the form does not offer other values.
      billingPeriod: 'monthly',
      sortOrder: values.sortOrder,
    });
  }

  const requiredRules = [
    {
      required: true,
      message: t('crm.admin.plans.field_required'),
    },
  ];

  return (
    <Modal
      title={isEdit ? t('crm.admin.plans.edit_title') : t('crm.admin.plans.create_title')}
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={isEdit ? t('crm.admin.plans.save') : t('crm.admin.plans.create')}
      cancelText={t('crm.common.cancel')}
      confirmLoading={pending}
      width={560}
      destroyOnHidden
      data-testid="admin-plan-form-modal"
    >
      {submitError !== null && submitError !== undefined ? (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          title={planFormErrorMessage(submitError, t)}
          description={submitError instanceof Error ? submitError.message : undefined}
        />
      ) : null}
      <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={pending}>
        <div className="flex gap-3">
          <Form.Item
            name="code"
            label={t('crm.admin.plans.field_code')}
            className="flex-1"
            extra={isEdit ? t('crm.admin.plans.code_locked_hint') : t('crm.admin.plans.code_hint')}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t('crm.admin.plans.code_required'),
              },
              {
                pattern: /^[a-z0-9-]+$/u,
                message: t('crm.admin.plans.code_pattern'),
              },
            ]}
          >
            <Input
              className="font-mono"
              placeholder="pro"
              maxLength={64}
              disabled={isEdit}
              aria-label={t('crm.admin.plans.field_code')}
            />
          </Form.Item>
          <Form.Item
            name="displayName"
            label={t('crm.admin.plans.field_name')}
            className="flex-1"
            rules={[
              {
                required: true,
                whitespace: true,
                message: t('crm.admin.plans.name_required'),
              },
            ]}
          >
            <Input
              placeholder={t('crm.admin.plans.name_placeholder')}
              maxLength={120}
              aria-label={t('crm.admin.plans.field_name')}
            />
          </Form.Item>
        </div>

        <div className="flex gap-3">
          <Form.Item
            name="price"
            label={t('crm.admin.plans.field_price')}
            className="flex-1"
            rules={requiredRules}
          >
            <InputNumber<number>
              className="w-full"
              min={0}
              step={1}
              precision={2}
              placeholder="20"
              addonAfter="$"
              aria-label={t('crm.admin.plans.field_price')}
            />
          </Form.Item>
          <Form.Item
            name="cost"
            label={t('crm.admin.plans.field_cost')}
            className="flex-1"
            rules={requiredRules}
          >
            <InputNumber<number>
              className="w-full"
              min={0}
              step={0.5}
              precision={2}
              placeholder="5"
              addonAfter="$"
              aria-label={t('crm.admin.plans.field_cost')}
            />
          </Form.Item>
        </div>

        <div className="flex gap-3">
          <Form.Item
            name="tokenLimitMonthly"
            label={t('crm.admin.plans.field_tokens')}
            className="flex-1"
            rules={requiredRules}
          >
            <InputNumber<number>
              className="w-full"
              min={0}
              step={1_000_000}
              precision={0}
              placeholder="50000000"
              formatter={(value) =>
                value === undefined || value === null
                  ? ''
                  : new Intl.NumberFormat(i18n.locale === 'en' ? 'en-US' : 'ru-RU').format(value)
              }
              parser={(display) => Number((display ?? '').replace(/[^\d]/gu, ''))}
              aria-label={t('crm.admin.plans.field_tokens')}
            />
          </Form.Item>
          <Form.Item
            name="storageGib"
            label={t('crm.admin.plans.field_storage')}
            className="flex-1"
            rules={requiredRules}
          >
            <InputNumber<number>
              className="w-full"
              min={0}
              step={0.5}
              placeholder="5"
              addonAfter={t('crm.admin.plans.storage_unit')}
              aria-label={t('crm.admin.plans.field_storage')}
            />
          </Form.Item>
        </div>

        <Form.Item name="sortOrder" label={t('crm.admin.plans.field_order')}>
          <InputNumber<number>
            className="w-full"
            precision={0}
            aria-label={t('crm.admin.plans.field_order')}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
